import { useState } from 'react'
import { Modal } from '@/components/lc'
import { RequestModal } from '@/components/RequestModal'
import { PinnedCardRail } from '@/components/ui/PinnedCardRail'
import { cn } from '@/lib/utils'
import { placeholderEvents, placeholderEventsFor, type PlaceholderEvent } from '@/data/placeholderEvents'

/* ==================================================================== *
 *  TEMPORARY placeholder events UI — remove with src/data/placeholderEvents.ts
 *  once the backend serves the real calendar.                           *
 *                                                                       *
 *  home  → pinned scroll-scrub rail (vertical scroll moves the cards     *
 *          sideways), matching the real backend events rail.            *
 *  community → a centred wrap of the events that fit that community.     *
 *  Each card has an "Express your interest" email popup and a "View more" *
 *  detail modal that repeats the same CTA.                              *
 * ==================================================================== */

type Props = {
  /** Community page name to filter by; omit for the full home set. */
  community?: string
  theme?: 'light' | 'dark'
  /** Optional heading + intro shown above the cards. */
  heading?: { kicker: string; title: string; intro?: string }
  /** Home uses the pinned scroll-scrub rail; community pages a centred wrap. */
  pinned?: boolean
  /** Section id for the pinned rail (home anchor). */
  railId?: string
  className?: string
}

export function PlaceholderEvents({ community, theme = 'dark', heading, pinned = false, railId, className }: Props) {
  const dark = theme === 'dark'
  const events = community ? placeholderEventsFor(community) : placeholderEvents
  const [detail, setDetail] = useState<PlaceholderEvent | null>(null)
  const [interest, setInterest] = useState<string | null>(null)
  if (events.length === 0) return null

  const inkText = dark ? 'text-white' : 'text-lc-ink'
  const muteText = dark ? 'text-white/65' : 'text-lc-mute'

  const headingNode = heading && (
    <div className="rail-heading container-lc px-5 pb-8 text-center">
      <p className={cn('label', muteText)}>{heading.kicker}</p>
      <h2 className={cn('mx-auto mt-3 max-w-[34ch] font-display text-[clamp(1.7rem,1.1rem+2.2vw,2.8rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText)}>{heading.title}</h2>
      {heading.intro && <p className={cn('rail-intro mx-auto mt-3 max-w-md text-[15px]', muteText)}>{heading.intro}</p>}
    </div>
  )

  const card = (e: PlaceholderEvent, i: number) => (
    <article
      key={e.key}
      data-rot={i % 2 ? 1 : -1}
      className={cn('ev-card group flex w-[300px] shrink-0 snap-start flex-col overflow-hidden rounded-[24px] !shadow-none transition-transform duration-300 ease-brand sm:w-[320px] xl:w-[352px]', 'lc-glass')}
    >
      <div className="ev-card-img relative h-[150px] shrink-0 overflow-hidden xl:h-[165px]">
        <img src={e.image} alt="" className="h-full w-full object-cover" loading="lazy" />
        <span className="label absolute left-3 top-3 rounded-pill bg-black/45 px-3 py-1.5 !text-[11px] text-white backdrop-blur">{e.where}</span>
      </div>
      <div className="flex flex-col p-5 xl:p-6">
        <h3 className={cn('font-display text-[1.4rem] font-normal leading-[1.1] tracking-[0.01em] xl:text-[1.55rem]', inkText)}>{e.title}</h3>
        <p className={cn('mt-2 line-clamp-2 text-[14px] leading-relaxed', muteText)}>{e.blurb}</p>
        <p className={cn('label mt-4 !text-[11px]', muteText)}>{e.when}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setInterest(e.title)}
            className="rounded-pill bg-lime px-4 py-2 text-[13px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.03] motion-reduce:hover:scale-100"
          >
            Express your interest
          </button>
          <button onClick={() => setDetail(e)} className="label !text-[11px] text-lc-accent underline-offset-2 hover:underline">
            View more
          </button>
        </div>
      </div>
    </article>
  )

  const modals = (
    <>
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.title ?? ''} size="lg" theme={theme}>
        {detail && (
          <div className="space-y-5">
            <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl">
              <img src={detail.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <p className="label !text-[11px] text-lc-accent">{detail.when} · {detail.where}</p>
            <p className="text-[15px] leading-relaxed text-lc-mute">{detail.long}</p>
            <div>
              <p className="label !text-[10px] text-lc-mute">Core pillar</p>
              <p className="mt-0.5 text-[14px] font-semibold text-lc-ink">{detail.pillar}</p>
            </div>
            <div className="pt-1">
              <button
                onClick={() => { const t = detail.title; setDetail(null); setInterest(t) }}
                className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
              >
                Express your interest
              </button>
            </div>
          </div>
        )}
      </Modal>
      <RequestModal kind="event" open={!!interest} onClose={() => setInterest(null)} subject={interest ?? undefined} theme={theme} />
    </>
  )

  // Home: pinned scroll-scrub rail (vertical scroll drives horizontal movement).
  if (pinned) {
    return (
      <>
        <PinnedCardRail id={railId} heading={headingNode} cards={events.map(card)} />
        {modals}
      </>
    )
  }

  // Community pages: a centred wrap of the events that fit.
  return (
    <div className={className}>
      {headingNode}
      {/* mobile: horizontal scroll rail showing a peek of the next card so the
          scroll is discoverable; md+: centred wrap since the few cards fit */}
      <div className={cn('no-scrollbar mt-2 flex snap-x items-start gap-5 overflow-x-auto px-5 pb-2 md:flex-wrap md:justify-center md:overflow-visible md:px-0', events.length === 1 && 'justify-center')}>
        {events.map(card)}
      </div>
      {modals}
    </div>
  )
}
